import React, { useEffect } from 'react';

import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PageHero from '../components/PageHero';
import SEO, { schemas } from '../components/SEO';
import './InnerPage.css';

const steps = [
  {
    n: '01',
    title: 'Configure Your Persona',
    subtitle: 'Connect Accugen to your AI system',
    text: 'Point Accugen to your AI chatbot or LLM API endpoint. Configure authentication headers, request body templates, and response field mappings. Save multiple personas for different models or environments — development, staging, and production.',
    bullets: [
      'REST API endpoint configuration',
      'Custom auth headers & tokens',
      'Request / response field mapping',
      'Multiple personas per project',
      'Environment-specific settings',
    ],
    icon: '/icons/workflow/workflow_41_configure.svg',
  },
  {
    n: '02',
    title: 'Select Your Test Library',
    subtitle: 'Choose from pre-built suites or upload your own',
    text: 'Browse the built-in library of 122+ OWASP-aligned security tests, 12-dimension bias evaluation suites, and quality benchmarks. Alternatively, upload your own domain-specific test cases via CSV for fully customised evaluation.',
    bullets: [
      '122+ OWASP security test cases',
      '12 bias dimension prompt suites',
      'Quality & hallucination benchmarks',
      'CSV upload for custom test cases',
      'Tag and filter test suites',
    ],
    icon: '/icons/workflow/workflow_42_test-library.svg',
  },
  {
    n: '03',
    title: 'Run Your Evaluation',
    subtitle: 'Parallel execution with real-time progress',
    text: 'Execute all selected test prompts concurrently. Accugen calls your AI endpoint for each test, captures the responses, and evaluates them using your configured scoring method — LLM-as-Judge, ROUGE, BLEU, or Semantic Similarity. Watch results populate in real time.',
    bullets: [
      'Parallel prompt execution engine',
      'Real-time progress dashboard',
      'Automatic retry on failures',
      'LLM-as-Judge & algorithmic scoring',
      'WAL-mode database for throughput',
    ],
    icon: '/icons/workflow/workflow_43_run-evaluation.svg',
  },
  {
    n: '04',
    title: 'Review & Export',
    subtitle: 'Interactive results and one-click PDF reports',
    text: 'Explore your results in the interactive dashboard. Drill into individual failures, compare bias variants side-by-side, filter by OWASP category or severity, and track improvement across evaluation runs. Export a full OWASP-mapped PDF compliance report with one click.',
    bullets: [
      'Interactive results dashboard',
      'Drill-down to individual failures',
      'Side-by-side bias variant comparison',
      'Filter by category & severity',
      'One-click OWASP-mapped PDF export',
    ],
    icon: '/icons/workflow/workflow_44_review-export.svg',
  },
];

export default function WorkflowPage() {
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); obs.unobserve(e.target); } }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <SEO
        title="Workflow"
        description="Get from configuration to full OWASP compliance report in 4 steps. Accugen's simple enterprise AI evaluation workflow: configure, select tests, run, export."
        canonical="/workflow"
        keywords="AI evaluation workflow, OWASP compliance steps, LLM testing process, AI security report generation"
        schema={schemas.howTo([
          {title:'Configure Persona',text:'Connect your AI endpoint and configure authentication and scoring.'},
          {title:'Select Test Library',text:'Choose from OWASP suites, bias tests, or upload custom CSV cases.'},
          {title:'Run Evaluation',text:'Parallel execution scores every prompt in real time.'},
          {title:'Review & Export',text:'Explore results and export OWASP-mapped PDF reports.'},
        ])}
      />
      <Header />

      <PageHero
        eyebrow="How It Works"
        title="From Setup to <em>Compliance</em><br/>in 4 Steps"
        subtitle="Simple, powerful workflow designed for enterprise AI teams of all sizes. No weeks of setup — start evaluating in minutes."
        crumb="Workflow"
      />

      {/* Timeline steps */}
      {steps.map((s, i) => (
        <section
          key={s.n}
          className={`ip-section${i % 2 === 1 ? ' ip-bg-lav' : ''}`}
        >
          <div className="container">
            <div className={`ip-split${i % 2 === 1 ? ' ip-split-rev' : ''}`}>
              <div>
                <div className="wf-step-num reveal">{s.n}</div>
                <h2 className="section-h reveal d1">{s.title}</h2>
                <p className="ip-feat-subtitle reveal d1">{s.subtitle}</p>
                <p className="ip-body reveal d2">{s.text}</p>
                <div className="ip-check-list reveal d3">
                  {s.bullets.map(b => (
                    <div key={b} className="ip-check-item">
                      <span className="ip-check-icon">✓</span>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
                {i === steps.length - 1 && (
                  <div className="ip-cta-inline reveal d4">
                    <a href="/contact" className="btn-navy" target="_blank" rel="noreferrer">Contact Us →</a>
                  </div>
                )}
              </div>
              <div className="ip-wf-visual reveal d2">
                <div className="iwv-num">{s.n}</div>
                <div className="iwv-icon"><img src={s.icon} alt={s.title} /></div>
                <div className="iwv-title">{s.title}</div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="ip-cta-band">
        <div className="container">
          <h2>Ready to Follow the Workflow?</h2>
          <p>Set up your first evaluation run in under 10 minutes.</p>
          <div className="ip-cta-btns">
            <a href="/contact" className="btn-navy" target="_blank" rel="noreferrer">Contact Us →</a>
            <Link to="/contact" className="btn-outline-navy">Book a Demo</Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
